/**
 * Public Terms of Use page (qdrives.in/terms).
 * Required for Meta WhatsApp Business / app review.
 * Static content. No auth, no data fetching, no logic.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

const UPDATED_ON = "29 June 2026";

function Section({ id, title, children }) {
  return (
    <section id={id} className="space-y-3" data-testid={`terms-section-${id}`}>
      <h2 className="text-base sm:text-lg font-display tracking-wide text-white">{title}</h2>
      <div className="text-sm text-white/70 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <main
      className="min-h-screen bg-[#0A0A0A] text-white px-4 sm:px-8 pt-8 pb-32"
      data-testid="terms-page"
    >
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="back-link"
          className="inline-flex items-center gap-2 text-[11px] font-display tracking-widest uppercase text-white/55 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Back to Home
        </Link>

        <header className="mt-6 mb-8 border-b border-[#222] pb-6">
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/45">Q Drives · Legal</div>
          <h1 className="text-3xl sm:text-4xl font-display tracking-wide text-white mt-2">Terms of Use</h1>
          <div className="text-xs text-white/45 font-mono-data mt-2">Last updated: {UPDATED_ON}</div>
        </header>

        <div className="space-y-8">
          <p className="text-sm text-white/70 leading-relaxed">
            These Terms of Use govern your access to and use of the Q Drives website
            (<span className="font-mono-data">qdrives.in</span>), our WhatsApp business
            channel, and any related vehicle acquisition services we offer
            (collectively, the “Services”). By using the Services you agree to these Terms.
            If you do not agree, please do not use the Services.
          </p>

          <Section id="use-of-website" title="1. Use of the Website and Services">
            <p>
              You agree to use the Services only for lawful purposes and only in connection with
              a genuine vehicle enquiry or general information request. You shall not attempt to
              probe, scan, compromise, or interfere with the Services or other users.
            </p>
          </Section>

          <Section id="no-purchase-guarantee" title="2. Vehicle Submission — No Guarantee of Purchase">
            <p>
              Submitting your vehicle details to Q Drives, whether through the website, WhatsApp,
              phone, or any other channel, does <strong>not</strong> constitute an offer or
              acceptance and does <strong>not</strong> create any obligation on Q Drives to purchase
              your vehicle. We may, in our sole discretion, decline to make an offer for any
              vehicle.
            </p>
          </Section>

          <Section id="indicative-offers" title="3. Offers, Valuations, and Pricing">
            <p>
              Any indicative price, valuation, estimated range, or offer communicated to you —
              whether on the website, via WhatsApp, by phone, by email, or in person — is
              <strong> indicative only </strong> and is expressly subject to:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Manual review by our evaluation team;</li>
              <li>Physical inspection of the vehicle by an authorised Q Drives representative;</li>
              <li>Verification of the Registration Certificate (RC) and all supporting documents;</li>
              <li>Verification of clear ownership and absence of liens, loans, or encumbrances;</li>
              <li>Final approval by Q Drives management.</li>
            </ul>
            <p>
              Q Drives does <strong>not</strong> guarantee an instant offer, a fixed price, or a
              purchase. Pricing, inspection, and purchase decisions remain manual at all times.
              Q Drives does not run automated chatbots, automated price negotiation, or automated
              valuation tools on its customer-facing channels.
            </p>
          </Section>

          <Section id="accuracy" title="4. Accuracy of Information You Provide">
            <p>
              You agree to provide accurate, current, and complete information about the vehicle
              and your ownership of it, including (without limitation) make, model, year, variant,
              kilometres driven, registration number, fuel type, transmission, accident or
              insurance history, and any modifications. You agree to share documents only if you
              are the lawful owner or are duly authorised by the owner.
            </p>
          </Section>

          <Section id="communications" title="5. Communications from Q Drives">
            <p>
              By submitting an enquiry, you agree that Q Drives may contact you by phone, SMS, or
              WhatsApp using the number you provided, in connection with your enquiry, inspection
              scheduling, paperwork, pickup, and post-sale operational matters. You may opt out of
              non-essential communications by writing to us.
            </p>
          </Section>

          <Section id="documentation" title="6. Documentation and Ownership Transfer">
            <p>
              Any purchase, where it occurs, is conditional on satisfactory completion of physical
              inspection, document verification (RC, insurance, NOC where applicable, PUC, ID
              proof, address proof, hypothecation closure, etc.), and execution of the relevant
              RTO ownership-transfer formalities. Q Drives may decline to proceed if any of these
              cannot be satisfactorily completed.
            </p>
          </Section>

          <Section id="ip" title="7. Intellectual Property">
            <p>
              All content on the Q Drives website — including text, graphics, logos, the Q Drives
              brand, and the underlying software — is owned by Q Drives or its licensors and is
              protected by applicable intellectual-property laws. You may not copy, modify,
              redistribute, or commercially exploit any portion of the Services without our prior
              written consent.
            </p>
          </Section>

          <Section id="third-party" title="8. Third-Party Services">
            <p>
              The Services rely on third-party providers (for example, hosting, storage, and
              messaging platforms such as Meta WhatsApp and Gupshup). Your use of those platforms
              is also subject to their respective terms and privacy policies. Q Drives is not
              responsible for outages, errors, or content originating from third-party providers.
            </p>
          </Section>

          <Section id="disclaimer" title="9. Disclaimer of Warranties">
            <p>
              The Services are provided on an “as is” and “as available” basis without warranties
              of any kind, whether express or implied, including but not limited to merchantability,
              fitness for a particular purpose, and non-infringement. We do not warrant that the
              Services will be uninterrupted, error-free, or free of viruses or other harmful
              components.
            </p>
          </Section>

          <Section id="liability" title="10. Limitation of Liability">
            <p>
              To the maximum extent permitted by law, Q Drives, its officers, employees, and
              affiliates shall not be liable for any indirect, incidental, special, consequential,
              or punitive damages arising out of or in connection with your use of the Services.
            </p>
          </Section>

          <Section id="governing-law" title="11. Governing Law">
            <p>
              These Terms are governed by the laws of India. Any dispute arising in connection
              with the Services shall be subject to the exclusive jurisdiction of the competent
              courts in Hyderabad, Telangana, India.
            </p>
          </Section>

          <Section id="changes" title="12. Changes to these Terms">
            <p>
              We may update these Terms from time to time. The “Last updated” date at the top of
              this page will reflect the most recent change. Your continued use of the Services
              after a change becomes effective constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section id="contact" title="13. Contact">
            <p>
              For questions regarding these Terms, please contact Q Drives via the support details
              published on our website or our WhatsApp business number.
            </p>
          </Section>

          <div className="text-[10px] text-white/35 font-mono-data tracking-widest uppercase pt-6 border-t border-[#1A1A1A]">
            © Q Drives · qdrives.in
          </div>
        </div>
      </div>
    </main>
  );
}
